import { Fragment, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import AbstractForm from '../forms/AbstractForm'
import theme from '../../constants/theme'
import User from '../../assets/svgs/user'
import BadgeCheck from '../../assets/svgs/BadgeCheck'
import SignalBars from '../../assets/svgs/SignalBars'
import Trophy from '../../assets/svgs/Trophy'
import FileText from '../../assets/svgs/FileText'
import { buildFeedbackSchema } from '../../schema/feedbackSchema'
import {
  buildCategoryFields,
  buildAnswers,
  computeProgress,
} from '../../utilities/feedbackForm'
import FeedbackSection from './FeedbackSection'
import FeedbackCandidateBanner from './FeedbackCandidateBanner'
import FeedbackProgressBar from './FeedbackProgressBar'

const ICON_PROPS = { width: 18, height: 18, color: theme.brandColorOrange }

const CATEGORIES = [
  {
    group: 'relationship',
    titleKey: 'relationshipToCandidate',
    Icon: BadgeCheck,
  },
  { group: 'performance', titleKey: 'performanceEvaluation', Icon: SignalBars },
  { group: 'recommendation', titleKey: 'overallRecommendation', Icon: Trophy },
  { group: 'comments', titleKey: 'additionalComments', Icon: FileText },
]

function FeedbackFormBody({ referenceDetails, formConfig, onSubmit }) {
  const { t, i18n } = useTranslation()
  const language = i18n.language

  const schema = useMemo(
    () => buildFeedbackSchema(formConfig, t),
    [formConfig, t],
  )

  const defaultValues = useMemo(() => {
    const details = referenceDetails || {}
    const referenceInfo = Object.fromEntries(
      Object.entries(details).filter(
        ([key]) => key !== 'candidateInfo' && key !== 'workDuration',
      ),
    )

    const dynamicDefaults = {}
    formConfig.forEach((q) => {
      if (q.fieldType === 'rating') return
      dynamicDefaults[q.key] = q.fieldType === 'inline-multiselect' ? [] : ''
    })

    return {
      ...dynamicDefaults,
      ...referenceInfo,
      work_duration: details.workDuration ?? '',
      confirmInformation: false,
    }
  }, [referenceDetails, formConfig])

  const [watchedValues, setWatchedValues] = useState(defaultValues)
  const handleFormWatch = useCallback((values) => {
    setWatchedValues(values || {})
  }, [])

  const progress = computeProgress(watchedValues)

  const relationshipOptions = useMemo(
    () => [
      {
        label: t('reference.relationship.directManager'),
        value: 'direct_manager',
      },
      { label: t('reference.relationship.teamLeader'), value: 'team_leader' },
      { label: t('reference.relationship.colleague'), value: 'colleague' },
      { label: t('reference.relationship.hr'), value: 'hr_representative' },
      { label: t('reference.relationship.client'), value: 'client' },
      { label: t('reference.relationship.other'), value: 'other' },
    ],
    [t],
  )

  const inputs = useMemo(() => {
    const yourInformation = [
      {
        name: 'fullname',
        label: t('reference.fullName'),
        required: true,
        width: 6,
        group: 'yourInformation',
      },
      {
        name: 'jobTitle',
        label: t('reference.jobTitle'),
        required: true,
        width: 6,
        group: 'yourInformation',
      },
      {
        name: 'companyName',
        label: t('reference.companyName'),
        required: true,
        width: 6,
        group: 'yourInformation',
      },
      {
        name: 'email',
        label: t('reference.email'),
        type: 'email',
        required: true,
        disabled: true,
        width: 6,
        group: 'yourInformation',
      },
      {
        name: 'phone',
        label: t('reference.phone'),
        required: true,
        disabled: true,
        width: 6,
        group: 'yourInformation',
      },
      {
        name: 'candidateRelationship',
        label: t('reference.relationshipToYou'),
        required: true,
        renderType: 'select',
        options: relationshipOptions,
        width: 6,
        group: 'yourInformation',
      },
    ]

    const dynamic = CATEGORIES.filter((c) =>
      formConfig.some((q) => q.category === c.group),
    ).flatMap((c) =>
      buildCategoryFields({
        formConfig,
        category: c.group,
        group: c.group,
        language,
        sectionValues: watchedValues,
        t,
      }),
    )

    const confirmation = [
      {
        name: 'confirmInformation',
        label: t('reference.confirmThatInformation'),
        renderType: 'checkbox',
        required: true,
        width: 12,
        group: 'confirmation',
      },
    ]

    return [...yourInformation, ...dynamic, ...confirmation]
  }, [formConfig, language, relationshipOptions, watchedValues, t])

  const groupMeta = useMemo(() => {
    const meta = {
      yourInformation: {
        title: t('reference.yourInformation'),
        icon: <User {...ICON_PROPS} />,
      },
    }
    CATEGORIES.forEach((c) => {
      meta[c.group] = {
        title: t(`reference.${c.titleKey}`),
        icon: <c.Icon {...ICON_PROPS} />,
      }
    })
    return meta
  }, [t])

  const renderGroup = (groupId, fields) => {
    const meta = groupMeta[groupId]

    if (groupId === 'yourInformation') {
      return (
        <Fragment key={groupId}>
          <FeedbackSection title={meta.title} icon={meta.icon}>
            {fields}
          </FeedbackSection>
          <FeedbackCandidateBanner
            items={[
              {
                label: t('reference.candidateName'),
                val: referenceDetails?.candidateInfo?.name,
              },
              {
                label: t('reference.position'),
                val: referenceDetails?.candidateInfo?.positionAppliedFor,
              },
              {
                label: t('reference.companyRequestionReference'),
                val: referenceDetails?.candidateInfo?.requestingCompany,
              },
            ]}
          />
        </Fragment>
      )
    }

    return (
      <FeedbackSection key={groupId} title={meta?.title} icon={meta?.icon}>
        {fields}
      </FeedbackSection>
    )
  }

  const handleSubmit = (data, setLoading) => {
    const payload = {
      fullname: data.fullname,
      job_title: data.jobTitle,
      company_name: data.companyName,
      email: data.email,
      phone: data.phone ?? '',
      candidate_relationship: data.candidateRelationship,
      answers: buildAnswers({ formConfig, values: data }),
    }
    onSubmit(payload, setLoading)
  }

  const actions = [
    {
      field: 'submit',
      text: t('reference.submitReferenceContacts'),
      onClick: handleSubmit,
    },
  ]

  return (
    <>
      <FeedbackProgressBar
        progress={progress}
        label={t('reference.complete')}
      />
      <AbstractForm
        width={12}
        inputs={inputs}
        schema={schema}
        defaultValues={defaultValues}
        actions={actions}
        handleFormWatch={handleFormWatch}
        resetOnSubmit={false}
        renderGroup={renderGroup}
      />
    </>
  )
}

FeedbackFormBody.propTypes = {
  referenceDetails: PropTypes.object,
  formConfig: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
}

export default FeedbackFormBody
